import styled, { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  @font-face {
    font-family: 'Miramar';
    src: url('/fonts/Miramar SmallCaps Regular.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: optional;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Cormorant Garamond', serif;
    font-variant-numeric: lining-nums;
    background-color: #ffffff;
    color: #2c2c2c;
    line-height: 1.8;
    font-weight: 300;
    letter-spacing: 0.3px;
    font-size: 16px;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }
  
  /* Add top spacing for pages to avoid header overlap */
  main {
    padding-top: 110px; /* Header (77px) + Announcement (33px) */

    @media (max-width: 768px) {
      padding-top: 90px; /* Header (60px) + Announcement (30px) */
    }
  }

  /* When announcement closed, less padding needed */
  body.announcement-closed main:not(.no-padding) {
    padding-top: 77px !important;

    @media (max-width: 768px) {
      padding-top: 60px !important;
    }
  }

  /* Home page needs padding for announcement bar + header */
  main:has([data-page="home"]) {
    padding-top: 110px;

    @media (max-width: 768px) {
      padding-top: 90px;
    }
  }

  /* When announcement closed, home page padding adjusts */
  body.announcement-closed main:has([data-page="home"]) {
    padding-top: 77px !important;

    @media (max-width: 768px) {
      padding-top: 60px !important;
    }
  }
  
  /* Guest checkout specific styles */
  main:has([data-page="guest-checkout"]) {
    padding-top: 110px; /* Header (77px) + Announcement (33px) */

    @media (max-width: 768px) {
      padding-top: 90px; /* Header (60px) + Announcement (30px) */
    }
  }

  /* When announcement closed, guest checkout padding adjusts */
  body.announcement-closed main:has([data-page="guest-checkout"]) {
    padding-top: 77px !important;

    @media (max-width: 768px) {
      padding-top: 60px !important;
    }
  }
  
  /* Admin panel specific styles - no top padding */
  .admin-app {
    margin: 0;
    padding: 0;
  }
  
  .admin-app main {
    padding-top: 0;
  }
  
  /* Dark mode scrollbar styles */
  body.admin-dark-mode {
    ::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    ::-webkit-scrollbar-track {
      background: #1a1a1a;
    }
    
    ::-webkit-scrollbar-thumb {
      background: #4a4a4a;
      border-radius: 4px;
    }
    
    ::-webkit-scrollbar-thumb:hover {
      background: #6a6a6a;
    }
  }
  
  html {
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    background-color: #ffffff;
  }

  /* Fix App container width on mobile when body has position: fixed */
  .App {
    @media (max-width: 768px) {
      width: 100vw;
      position: relative;
    }
  }
  
  /* Desktop scrollbar styling */
  @media (min-width: 769px) {
    #gallery::-webkit-scrollbar,
    .gallery-scrollbar::-webkit-scrollbar,
    section[data-scroll-container]::-webkit-scrollbar {
      width: 2px !important;
    }
    
    #gallery::-webkit-scrollbar-track,
    .gallery-scrollbar::-webkit-scrollbar-track,
    section[data-scroll-container]::-webkit-scrollbar-track {
      background: transparent !important;
    }
    
    #gallery::-webkit-scrollbar-thumb,
    .gallery-scrollbar::-webkit-scrollbar-thumb,
    section[data-scroll-container]::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.15) !important;
      border-radius: 1px !important;
    }
    
    #gallery::-webkit-scrollbar-thumb:hover,
    .gallery-scrollbar::-webkit-scrollbar-thumb:hover,
    section[data-scroll-container]::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.25) !important;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Cormorant Garamond', serif;
    font-weight: 300;
    line-height: 1.2;
    letter-spacing: -0.01em;
    color: var(--color-charcoal);
    margin: 0;
  }
  
  h1 {
    font-size: clamp(1.75rem, 3vw, 2rem);
    line-height: 1.2;
    margin-bottom: 1rem;
    font-weight: 300;
  }
  
  h2 {
    font-size: clamp(1.5rem, 2.5vw, 1.75rem);
    line-height: 1.3;
    margin-bottom: 0.75rem;
    font-weight: 300;
  }
  
  h3 {
    font-size: clamp(1.25rem, 2vw, 1.5rem);
    margin-bottom: 0.5rem;
    font-weight: 300;
  }
  
  h4 {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
    font-weight: 300;
  }
  
  p {
    font-size: 0.9375rem;
    line-height: 1.7;
    margin-bottom: 1rem;
    color: var(--color-text-secondary);
    font-weight: 300;
  }
  
  /* Global button styles */
  button, .btn {
    font-family: 'Cormorant Garamond', serif;
    font-weight: 300;
    letter-spacing: 0.05em;
  }
  
  /* Global link styles */
  a {
    font-weight: 300;
    transition: var(--transition-base);
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
  }

  input, textarea, select {
    font-family: inherit;
    font-variant-numeric: lining-nums;
    border: none;
    outline: none;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 var(--spacing-lg);
  }
  
  @media (max-width: 768px) {
    .container {
      padding: 0 var(--spacing-md);
    }
  }

  .section {
    padding: var(--spacing-3xl) 0;
  }
  
  @media (max-width: 768px) {
    .section {
      padding: var(--spacing-2xl) 0;
    }
  }

  .text-center {
    text-align: center;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Refined luxury color palette */
  :root {
    --color-black: #1a1a1a;
    --color-white: #ffffff;
    --color-off-white: #fefefe;
    --color-gray-light: #f8f8f6;
    --color-gray-medium: #e6e4e1;
    --color-gray-dark: #6b6b6b;
    --color-gold: #c9a961;
    --color-gold-light: #e8d5a6;
    --color-gold-dark: #a08246;
    --color-success: #22c55e;
    --color-error: #dc2626;
    --color-warning: #f59e0b;
    --color-cream: #faf8f5;
    --color-charcoal: #2c2c2c;
    --color-brown: #8b6f47;
    --color-brown-light: #c4a373;
    --color-brown-dark: #6d4c2a;
    --color-beige: #f7f3ed;
    --color-warm-gray: #f9f7f4;
    --color-accent: #e8ddd4;
    --color-text-primary: #2c2c2c;
    --color-text-secondary: #4a4a4a;
    --color-text-muted: #6b6b6b;
    --spacing-xs: 0.5rem;
    --spacing-sm: 1rem;
    --spacing-md: 2rem;
    --spacing-lg: 3rem;
    --spacing-xl: 4rem;
    --spacing-2xl: 6rem;
    --spacing-3xl: 8rem;
    --transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    --transition-slow: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.05);
    --shadow-md: 0 4px 16px rgba(0, 0, 0, 0.08);
    --shadow-lg: 0 8px 32px rgba(0, 0, 0, 0.12);
    --shadow-xl: 0 16px 48px rgba(0, 0, 0, 0.16);
  }

  /* Modern animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  .fade-in {
    animation: fadeIn 0.6s ease-out;
  }

  .slide-in {
    animation: slideIn 0.4s ease-out;
  }

  /* Responsive breakpoints */
  /* Enhanced focus states */
  *:focus {
    outline: none;
  }
  
  *:focus-visible {
    outline: 2px solid var(--color-gold);
    outline-offset: 2px;
  }
  
  /* Instant scroll for page navigation - no animation */
  html {
    scroll-behavior: auto;
  }
  
  /* Selection color - Prada style baby blue */
  ::selection {
    background-color: #D4E7F5;
    color: var(--color-charcoal);
  }

  ::-moz-selection {
    background-color: #D4E7F5;
    color: var(--color-charcoal);
  }
  
  /* Cursor enhancements */
  * {
    cursor: inherit;
  }
  
  a, button, [role="button"] {
    cursor: pointer;
  }
  
  /* Page transitions */
  .page-enter {
    opacity: 0;
    transform: translateY(20px);
  }
  
  .page-enter-active {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }
  
  .page-exit {
    opacity: 1;
    transform: translateY(0);
  }
  
  .page-exit-active {
    opacity: 0;
    transform: translateY(-20px);
    transition: opacity 0.4s ease, transform 0.4s ease;
  }
  
  /* Luxury hover effects */
  .luxury-hover {
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.1),
        transparent
      );
      transition: left 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    &:hover::before {
      left: 100%;
    }
  }
  
  /* Smooth image loading */
  img {
    opacity: 0;
    animation: imageLoad 0.6s ease forwards;
  }
  
  @keyframes imageLoad {
    to {
      opacity: 1;
    }
  }
  
  /* Luxury text reveal */
  .text-reveal {
    overflow: hidden;
    
    span {
      display: inline-block;
      transform: translateY(100%);
      animation: textReveal 0.8s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    }
  }
  
  @keyframes textReveal {
    to {
      transform: translateY(0);
    }
  }
`;

export const Button = styled.button<{
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'hero-outline';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => 
    props.size === 'small' ? '10px 24px' :
    props.size === 'large' ? '18px 48px' :
    '14px 36px'
  };
  font-size: ${props => 
    props.size === 'small' ? '0.8125rem' :
    props.size === 'large' ? '1rem' :
    '0.875rem'
  };
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 1px solid transparent;
  border-radius: 0;
  transition: var(--transition-base);
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  min-height: 48px;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background-color: var(--color-charcoal);
          color: var(--color-off-white);
          border-color: var(--color-charcoal);
          
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: var(--color-gold);
            transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          &:hover {
            border-color: var(--color-gold);
            
            &::before {
              left: 0;
            }
          }
          
          span {
            position: relative;
            z-index: 1;
          }
        `;
      case 'secondary':
        return `
          background-color: var(--color-gold);
          color: var(--color-off-white);
          border-color: var(--color-gold);
          
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, transparent 40%, rgba(255, 255, 255, 0.3) 50%, transparent 60%);
            transform: translateX(-100%);
            transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          &:hover {
            background-color: var(--color-gold-dark);
            border-color: var(--color-gold-dark);
            transform: translateY(-1px);
            box-shadow: var(--shadow-lg);
            
            &::before {
              transform: translateX(100%);
            }
          }
          
          span {
            position: relative;
            z-index: 1;
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: var(--color-charcoal);
          border-color: var(--color-charcoal);
          
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 0;
            height: 100%;
            background: #000000;
            transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          &:hover {
            color: var(--color-off-white);
            transform: translateY(-1px);
            box-shadow: var(--shadow-md);
            
            &::before {
              width: 100%;
            }
          }
          
          span {
            position: relative;
            z-index: 1;
          }
        `;
      case 'hero-outline':
        return `
          background-color: transparent;
          color: var(--color-off-white);
          border-color: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(5px);
          
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 0;
            height: 100%;
            background: rgba(255, 255, 255, 0.95);
            transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          &:hover {
            border-color: var(--color-white);
            color: var(--color-charcoal);
            transform: translateY(-1px);
            box-shadow: var(--shadow-lg);
            
            &::before {
              width: 100%;
            }
          }
          
          span {
            position: relative;
            z-index: 1;
          }
        `;
      case 'ghost':
        return `
          background-color: transparent;
          color: var(--color-text-secondary);
          border-color: transparent;
          
          &:hover {
            color: var(--color-charcoal);
            background-color: var(--color-beige);
            transform: translateY(-1px);
          }
        `;
      default:
        return `
          background-color: var(--color-charcoal);
          color: var(--color-off-white);
          border-color: var(--color-charcoal);
          
          &:hover {
            background-color: var(--color-black);
            border-color: var(--color-black);
            transform: translateY(-1px);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
    }
  }

  &:focus-visible {
    outline: 2px solid var(--color-gold);
    outline-offset: 2px;
  }
  
  /* Fix for button text */
  & > * {
    position: relative;
    z-index: 1;
  }
`;

export const Input = styled.input<{
  variant?: 'default' | 'search';
  hasError?: boolean;
}>`
  width: 100%;
  padding: 14px 20px;
  font-size: 0.9375rem;
  font-weight: 300;
  letter-spacing: 0.02em;
  background-color: var(--color-white);
  border: 1px solid ${props => props.hasError ? 'var(--color-error)' : 'var(--color-gray-medium)'};
  border-radius: 0;
  transition: var(--transition-base);
  color: var(--color-charcoal);

  &:focus {
    border-color: var(--color-gold);
    background-color: var(--color-off-white);
    outline: none;
  }

  &::placeholder {
    color: var(--color-text-muted);
    font-weight: 300;
  }
  
  &:hover:not(:focus) {
    border-color: var(--color-gray-dark);
  }

  ${props => props.variant === 'search' && `
    padding-left: 40px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3E%3C/svg%3E");
    background-position: 12px center;
    background-repeat: no-repeat;
    background-size: 20px;
  `}
`;

export const Card = styled.div<{
  variant?: 'default' | 'elevated' | 'bordered';
  padding?: 'small' | 'medium' | 'large';
}>`
  background-color: var(--color-white);
  border-radius: 0;
  overflow: hidden;
  transition: var(--transition-base);
  
  ${props => {
    switch (props.variant) {
      case 'elevated':
        return `
          box-shadow: var(--shadow-md);
          background: linear-gradient(to bottom, var(--color-white), var(--color-off-white));
          
          &:hover {
            box-shadow: var(--shadow-lg);
            transform: translateY(-2px);
          }
        `;
      case 'bordered':
        return `
          border: 1px solid var(--color-gray-medium);
          background: var(--color-off-white);
          
          &:hover {
            border-color: var(--color-gold-light);
            box-shadow: var(--shadow-sm);
          }
        `;
      default:
        return `
          background: var(--color-white);
          box-shadow: var(--shadow-sm);
        `;
    }
  }}

  ${props => {
    switch (props.padding) {
      case 'small':
        return 'padding: var(--spacing-sm);';
      case 'large':
        return 'padding: var(--spacing-lg);';
      default:
        return 'padding: var(--spacing-md);';
    }
  }}
`;

export const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 2px solid var(--color-gray-light);
  border-top: 2px solid var(--color-gold);
  border-radius: 50%;
  animation: spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
  margin: var(--spacing-lg) auto;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 36px;
    height: 36px;
    border: 2px solid transparent;
    border-top: 2px solid var(--color-gold-light);
    border-radius: 50%;
    animation: spin 1.5s linear infinite reverse;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;